/**
 * WeatherChecklistItem.js
 *
 * Inline weather input with expandable big-button selectors.
 * Follows same pattern as FuelChecklistItem.
 * Designed for glove-friendly operation with 56px minimum tap targets.
 *
 * Props:
 * - value: { windDirection, windSpeed, seaState, visibility }
 * - onChange: (weather) => void
 * - disabled: boolean (default false)
 * - hideLabel: boolean (default false) - hides "Weather" label when used inside a card with its own title
 *
 * Usage:
 * <WeatherChecklistItem
 *   value={weather}
 *   onChange={handleWeatherChange}
 *   hideLabel={true}  // When card has its own title
 * />
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Cloud, Wind, Navigation, Waves, Eye, Check, ChevronDown, ChevronUp } from 'lucide-react';

// Wind speed ranges (from WeatherInputModal)
const WIND_SPEEDS = [
  { value: 'calm', label: 'Calm', range: '0-5', knots: 0, color: 'green' },
  { value: 'light', label: 'Light', range: '6-15', knots: 10, color: 'green' },
  { value: 'moderate', label: 'Mod', range: '16-25', knots: 20, color: 'cyan' },
  { value: 'fresh', label: 'Fresh', range: '26-35', knots: 30, color: 'amber' },
  { value: 'strong', label: 'Strong', range: '36+', knots: 40, color: 'red' }
];

// Sea state (Beaufort scale)
const SEA_STATES = [
  { value: 'calm', label: 'Calm', beaufort: '0-1', color: 'green' },
  { value: 'slight', label: 'Slight', beaufort: '2-3', color: 'green' },
  { value: 'moderate', label: 'Mod', beaufort: '4', color: 'cyan' },
  { value: 'rough', label: 'Rough', beaufort: '5-6', color: 'amber' },
  { value: 'very_rough', label: 'V.Rough', beaufort: '7+', color: 'red' }
];

// Visibility ranges
const VISIBILITY = [
  { value: 'good', label: 'Good', range: '>5nm', color: 'green' },
  { value: 'moderate', label: 'Moderate', range: '2-5nm', color: 'cyan' },
  { value: 'poor', label: 'Poor', range: '0.5-2nm', color: 'amber' },
  { value: 'fog', label: 'Fog', range: '<0.5nm', color: 'red' }
];

// Compass rose layout
const COMPASS_ROSE = [
  ['NW', 'N', 'NE'],
  ['W', 'VAR', 'E'],
  ['SW', 'S', 'SE']
];

// Color utility for buttons
const getButtonColor = (colorName, isSelected) => {
  const colors = {
    green: isSelected
      ? 'bg-green-600 border-green-700 ring-2 ring-green-300'
      : 'bg-green-500 hover:bg-green-600 border-green-600',
    cyan: isSelected
      ? 'bg-cyan-600 border-cyan-700 ring-2 ring-cyan-300'
      : 'bg-cyan-500 hover:bg-cyan-600 border-cyan-600',
    amber: isSelected
      ? 'bg-amber-600 border-amber-700 ring-2 ring-amber-300'
      : 'bg-amber-500 hover:bg-amber-600 border-amber-600',
    red: isSelected
      ? 'bg-red-600 border-red-700 ring-2 ring-red-300'
      : 'bg-red-500 hover:bg-red-600 border-red-600',
    slate: isSelected
      ? 'bg-slate-700 border-slate-700 ring-2 ring-slate-300'
      : 'bg-slate-400 hover:bg-slate-500 border-slate-500'
  };
  return colors[colorName] || colors.slate;
};

const WeatherChecklistItem = ({
  value = {},
  onChange,
  disabled = false,
  hideLabel = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract current values
  const { windDirection = '', windSpeed = '', seaState = '', visibility = '' } = value || {};

  // Check if we have any data
  const hasData = windDirection || windSpeed || seaState || visibility;

  // Update a single field
  const updateField = useCallback((field, fieldValue) => {
    onChange?.({
      ...value,
      [field]: fieldValue
    });
  }, [value, onChange]);

  // Format summary for collapsed view
  const getSummary = useMemo(() => {
    const parts = [];

    if (windDirection) {
      let windPart = `Wind: ${windDirection}`;
      if (windSpeed) {
        const speed = WIND_SPEEDS.find(s => s.value === windSpeed);
        windPart += `, ${speed?.label || windSpeed}`;
      }
      parts.push(windPart);
    } else if (windSpeed) {
      const speed = WIND_SPEEDS.find(s => s.value === windSpeed);
      parts.push(`Wind: ${speed?.label || windSpeed}`);
    }

    if (seaState) {
      const sea = SEA_STATES.find(s => s.value === seaState);
      parts.push(`Sea: ${sea?.label || seaState}`);
    }

    if (visibility) {
      const vis = VISIBILITY.find(v => v.value === visibility);
      parts.push(`Vis: ${vis?.label || visibility}`);
    }

    return parts.join(' · ') || 'Tap to set weather';
  }, [windDirection, windSpeed, seaState, visibility]);

  return (
    <div className={`rounded-lg border-2 overflow-hidden transition-colors ${
      hasData
        ? 'bg-green-50 border-green-200'
        : 'bg-white border-slate-200'
    }`}>
      {/* Header row - always visible */}
      <button
        type="button"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        disabled={disabled}
        className={`w-full flex items-center gap-3 p-3 min-h-[56px] text-left transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-slate-50'
        }`}
      >
        {/* Check circle */}
        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          hasData
            ? 'bg-green-500 border-green-500'
            : 'border-gray-300'
        }`}>
          {hasData && <Check className="h-4 w-4 text-white" />}
        </div>

        {/* Label and value */}
        <div className="flex-1 min-w-0">
          {!hideLabel && (
            <div className="flex items-center gap-2">
              <Cloud className={`h-4 w-4 ${hasData ? 'text-green-600' : 'text-slate-400'}`} />
              <p className={`font-medium ${hasData ? 'text-green-700' : 'text-gray-900'}`}>
                Weather
              </p>
              <span className="text-xs text-gray-400">(optional)</span>
            </div>
          )}
          <p className={`text-sm ${hideLabel ? '' : 'mt-0.5'} ${hasData ? 'text-green-600' : 'text-slate-500'}`}>
            {getSummary}
          </p>
        </div>

        {/* Chevron */}
        {!disabled && (
          isExpanded
            ? <ChevronUp className="h-5 w-5 text-slate-400" />
            : <ChevronDown className="h-5 w-5 text-slate-400" />
        )}
      </button>

      {/* Expanded content - weather selection */}
      {isExpanded && !disabled && (
        <div className="px-3 pb-4 border-t border-slate-100 space-y-4">

          {/* Wind Direction - Compass Rose */}
          <div className="pt-3">
            <div className="flex items-center gap-2 mb-2">
              <Navigation className="h-4 w-4 text-slate-500" />
              <label className="text-xs font-medium text-gray-600">Wind Direction</label>
            </div>

            {/* Compact Compass Rose */}
            <div className="grid grid-cols-3 gap-1.5 max-w-[180px] mx-auto mb-2">
              {COMPASS_ROSE.flat().map((dir) => {
                const isCenter = dir === 'VAR';
                const isCardinal = ['N', 'S', 'E', 'W'].includes(dir);
                const displayDir = dir === 'VAR' ? 'Variable' : dir;
                const isSelected = windDirection === displayDir;
                return (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => updateField('windDirection', displayDir)}
                    className={`
                      h-11 rounded-lg border-2 transition-all
                      flex items-center justify-center text-sm font-bold
                      ${isSelected
                        ? 'bg-cyan-600 border-cyan-700 ring-2 ring-cyan-300 text-white'
                        : isCenter
                          ? 'bg-slate-200 border-slate-300 text-slate-600 hover:bg-slate-300'
                          : isCardinal
                            ? 'bg-cyan-100 border-cyan-200 text-cyan-700 hover:bg-cyan-200'
                            : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                      }
                    `}
                  >
                    {dir === 'VAR' ? 'Var' : dir}
                  </button>
                );
              })}
            </div>

            {/* Calm button */}
            <button
              type="button"
              onClick={() => updateField('windDirection', 'Calm')}
              className={`
                w-full max-w-[180px] mx-auto block h-10 rounded-lg border-2 transition-all text-sm font-medium
                ${windDirection === 'Calm'
                  ? 'bg-green-600 border-green-700 ring-2 ring-green-300 text-white'
                  : 'bg-green-100 border-green-200 text-green-700 hover:bg-green-200'
                }
              `}
            >
              Calm
            </button>
          </div>

          {/* Wind Speed */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wind className="h-4 w-4 text-slate-500" />
              <label className="text-xs font-medium text-gray-600">Wind Speed</label>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {WIND_SPEEDS.map((speed) => (
                <button
                  key={speed.value}
                  type="button"
                  onClick={() => updateField('windSpeed', speed.value)}
                  className={`
                    h-12 rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center text-white
                    ${getButtonColor(speed.color, windSpeed === speed.value)}
                  `}
                >
                  <span className="text-xs font-bold">{speed.label}</span>
                  <span className="text-[10px] opacity-80">{speed.range}kt</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sea State */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Waves className="h-4 w-4 text-slate-500" />
              <label className="text-xs font-medium text-gray-600">Sea State</label>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {SEA_STATES.map((state) => (
                <button
                  key={state.value}
                  type="button"
                  onClick={() => updateField('seaState', state.value)}
                  className={`
                    h-12 rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center text-white
                    ${getButtonColor(state.color, seaState === state.value)}
                  `}
                >
                  <span className="text-xs font-bold">{state.label}</span>
                  <span className="text-[10px] opacity-80">BF {state.beaufort}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Visibility */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-slate-500" />
              <label className="text-xs font-medium text-gray-600">Visibility</label>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {VISIBILITY.map((vis) => (
                <button
                  key={vis.value}
                  type="button"
                  onClick={() => updateField('visibility', vis.value)}
                  className={`
                    h-12 rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center text-white
                    ${getButtonColor(vis.color, visibility === vis.value)}
                  `}
                >
                  <span className="text-xs font-bold">{vis.label}</span>
                  <span className="text-[10px] opacity-80">{vis.range}</span>
                </button>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default WeatherChecklistItem;
